/**
 * The set of detached surfaces this page is showing *itself* (架构方案 §5.8 通用
 * Detachable), for a runtime that could not give them a window of their own.
 *
 * A browser refuses `window.open` without transient user activation, so a
 * surface a module opens on its own — an incoming call — would otherwise have
 * nowhere to go. The host's answer is to hand it back to a page, which floats it
 * above the workbench; from the extension host's point of view the surface is
 * detached either way, so `attach` / `focus` / `list` and the detached broadcast
 * all keep working unchanged.
 *
 * Deliberately **not** `context.overlay`: an overlay is a temporary layer a
 * module asks for, whereas this is the platform standing in for a window the
 * runtime cannot open. Modules never see it.
 *
 * State lives here rather than in the component so it outlives any remount of
 * the workbench (a raised AppGate replaces the whole tree, 见 8). The truthful
 * teardown is the page itself going away, which the host reads from the
 * connection's Web Lock — never from a React unmount.
 */

/** One surface being shown in-page, plus the frame geometry to show it with. */
export interface InlineDetachedSurface {
  /**
   * Host-assigned identity, opaque here (it is the driver's window name). Echoed
   * back when the frame closes so the host can drop the right entry.
   */
  id: string;
  viewId: string;
  moduleId: string;
  fragmentId?: string;
  instanceKey?: string;
  payload?: unknown;
  width?: number;
  height?: number;
  minWidth?: number;
  minHeight?: number;
  /** Whether the user may resize the frame. Default `true`. */
  resizable?: boolean;
}

export interface InlineDetachedStore {
  /** Show a surface, or re-focus the frame already showing it. */
  mount(surface: InlineDetachedSurface): void;
  /** Drop a frame and report it closed — whoever asked, host or user. */
  unmount(id: string): void;
  /** Bring a frame to the top of the floating stack. */
  raise(id: string): void;
  /**
   * A frame went away. One event for both directions (the host asking and the
   * user closing it), because the host drops its entry when the surface is
   * *actually* gone rather than when it asked — exactly as a detached window's
   * entry waits for the window to report itself closed.
   */
  onClosed(listener: (id: string) => void): () => void;
  subscribe(
    listener: (surfaces: readonly InlineDetachedSurface[]) => void,
  ): () => void;
  /** Current stack, bottom-first. */
  list(): readonly InlineDetachedSurface[];
}

export function createInlineDetachedStore(): InlineDetachedStore {
  let surfaces: readonly InlineDetachedSurface[] = [];
  const changeListeners = new Set<
    (surfaces: readonly InlineDetachedSurface[]) => void
  >();
  const closedListeners = new Set<(id: string) => void>();

  const publish = (next: readonly InlineDetachedSurface[]): void => {
    surfaces = next;
    for (const listener of [...changeListeners]) {
      listener(surfaces);
    }
  };

  const raise = (id: string): void => {
    const found = surfaces.find((entry) => entry.id === id);
    if (!found || surfaces[surfaces.length - 1] === found) {
      return;
    }
    publish([...surfaces.filter((entry) => entry.id !== id), found]);
  };

  return {
    mount(surface) {
      if (surfaces.some((entry) => entry.id === surface.id)) {
        // Idempotent per handle, like the host's own `open`: showing the same
        // surface twice raises the one frame instead of stacking a second.
        raise(surface.id);
        return;
      }
      publish([...surfaces, surface]);
    },
    unmount(id) {
      if (!surfaces.some((entry) => entry.id === id)) {
        return;
      }
      publish(surfaces.filter((entry) => entry.id !== id));
      for (const listener of [...closedListeners]) {
        listener(id);
      }
    },
    raise,
    onClosed(listener) {
      closedListeners.add(listener);
      return () => closedListeners.delete(listener);
    },
    subscribe(listener) {
      changeListeners.add(listener);
      return () => changeListeners.delete(listener);
    },
    list: () => surfaces,
  };
}
